import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import { RefreshCw, Trash2 } from 'lucide-react'
import consumer from '@/lib/cable'
import type { SalesminerOrganizationUpload as Upload } from '@/types/serializers'
import StatusCard from './_components/StatusCard'
import FileInfoCard from './_components/FileInfoCard'
import ErrorsCard from './_components/ErrorsCard'
import FlashMessages from './_components/FlashMessages'
import ImportReportCard from './_components/ImportReportCard'

interface Props {
    upload: Upload
    message?: string
    flash?: {
        notice?: string
        alert?: string
    }
}


export default function Show({upload, message, flash}: Props) {
    const [currentUpload, setCurrentUpload] = useState(upload)
    const [isConnected, setIsConnected] = useState(false)

    // Sync prop changes to local state (handles router.reload updates)
    useEffect(() => {
        setCurrentUpload(upload)
    }, [upload])

    // ActionCable subscription for real-time updates
    useEffect(() => {
        const subscription = consumer.subscriptions.create(
            {channel: "OrganizationUploadChannel", upload_id: currentUpload.id},
            {
                connected() {
                    console.log("Connected to OrganizationUploadChannel")
                    setIsConnected(true)
                },

                disconnected() {
                    console.log("Disconnected from OrganizationUploadChannel")
                    setIsConnected(false)
                },

                received(data: any) {
                    console.log("Received ActionCable update:", data)

                    // Update the upload status based on received data
                    setCurrentUpload(prev => ({
                        ...prev,
                        status: data.status || prev.status,
                        records_processed: data.records_processed || prev.records_processed,
                        total_records: data.total_records || prev.total_records,
                        progress_percentage: data.progress_percentage || prev.progress_percentage,
                        // Update import_report if it's provided via ActionCable
                        import_report: data.import_report || prev.import_report
                    }))

                    // If status changed to completed or failed, reload full data
                    if (data.status === 'completed' || data.status === 'failed') {
                        setTimeout(() => {
                            router.reload({only: ['upload']})
                        }, 500)
                    }
                }
            }
        )

        return () => {
            subscription.unsubscribe()
        }
    }, [currentUpload.id])


    const handleRetry = async () => {
        try {
            await router.post(`/admin/organization_uploads/${currentUpload.id}/retry`, {}, {
                preserveState: true,
                onSuccess: () => {
                    router.reload()
                }
            })
        } catch (error) {
            console.error('Retry failed:', error)
        }
    }

    const handleDelete = async () => {
        if (confirm('Are you sure you want to delete this upload? This action cannot be undone.')) {
            router.delete(`/admin/organization_uploads/${currentUpload.id}`)
        }
    }

    return (
        <>
            <Head title={`Upload: ${currentUpload.filename} - Super Admin`}/>

            <div className="min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white shadow">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center py-6">
                            <div className="flex items-center space-x-4">
                                <Link href="/admin/organization_uploads"
                                      className="text-indigo-600 hover:text-indigo-700">
                                    ← Back to Uploads
                                </Link>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    Upload Details
                                </h1>
                                <div className="flex items-center space-x-2">
                                    {isConnected && (
                                        <div className="flex items-center text-green-600 text-sm">
                                            <span className="relative flex size-3 mr-1">  <span
                                                className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>  <span
                                                className="relative inline-flex size-3 rounded-full bg-green-500"></span></span>
                                            Live Updates
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center space-x-3">
                                {currentUpload.status === 'failed' && (
                                    <button
                                        onClick={handleRetry}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors inline-flex items-center"
                                    >
                                        <RefreshCw className="w-4 h-4 mr-2"/>
                                        Retry
                                    </button>
                                )}
                                <button
                                    onClick={handleDelete}
                                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors inline-flex items-center"
                                >
                                    <Trash2 className="w-4 h-4 mr-2"/>
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-5xl mx-auto py-6 sm:px-6 lg:px-8">
                    <div className="px-4 py-6 sm:px-0">
                        <FlashMessages message={message} flash={flash}/>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-1">
                                <StatusCard upload={currentUpload}/>
                            </div>

                            <div className="lg:col-span-2 space-y-6">
                                <FileInfoCard upload={currentUpload}/>

                                <ImportReportCard upload={currentUpload}/>

                                <ErrorsCard errorMessages={currentUpload.error_messages || []}/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}